import React, { FunctionComponent, useCallback, useState } from 'react';
import { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';
import FileUpload, { FileToUpload, FileType } from 'ui/atoms/file-upload';

interface SingleFileUploadProps {
  onChange: (arg: File | FileToUpload | null) => void;
  onFocus?: (arg: any) => void;
  required?: boolean;
  showRequiredError?: boolean;
  documentTypes?: Array<any>;
  valid?: boolean;
  value: File | null;
  fileType?: FileType;
  isDocumentTypeRequired?: boolean;
}

export const SingleFileUpload: FunctionComponent<SingleFileUploadProps> = ({
  onChange = () => {},
  value,
  documentTypes,
  showRequiredError,
  fileType = 'pdf',
  isDocumentTypeRequired,
}) => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(value);
  const [documentType, setDocumentType] = useState<string>();
  const [error, setError] = useState('');
  const translate = useTranslateWithStringArgs();

  const handleFileInput = (event: any) => {
    event.preventDefault();
    setError('');
    const file = event.target?.files?.length ? event.target?.files[0] : event.dataTransfer.files[0];

    if (file) {
      let hasValidFormat;

      switch (fileType) {
        case 'pdf':
          hasValidFormat = file.type === 'application/pdf';
          break;
        case 'csv':
          hasValidFormat =
            file.type === 'text/csv' || file.type === 'text/plain' || file.name.toLowerCase().endsWith('.csv');
          break;
        default:
          hasValidFormat = file.type.startsWith('image/');
          break;
      }

      const hasValidSize = file?.size < 5000000;

      if (hasValidFormat && hasValidSize) {
        setUploadedFile(file);
        onChange({ file, documentType });
      } else if (!hasValidFormat) {
        const errorKeyMap = {
          pdf: 'form.errors.fileTypePdf',
          csv: 'form.errors.fileTypeCsv',
          image: 'form.errors.fileTypeImage',
        };

        const errorKey = errorKeyMap[fileType] || 'form.errors.fileTypePdf';
        setError(translate(errorKey));
      } else {
        setError(translate('form.errors.fileSize'));
      }
    }
  };

  const onSelectDocumentType = useCallback(
    (documentType: string) => {
      setDocumentType(documentType);
      onChange({ file: uploadedFile, documentType });
    },
    [uploadedFile],
  );

  const onRemoveDocument = () => {
    setUploadedFile(null);
    setDocumentType('');
    onChange(null);
    setError('');
  };

  return (
    <FileUpload
      handleFileInput={handleFileInput}
      uploadedFiles={[{ file: uploadedFile, documentType }]}
      onRemoveDocument={onRemoveDocument}
      documentTypes={documentTypes}
      showRequiredError={showRequiredError}
      onSelectDocumentType={onSelectDocumentType}
      fileType={fileType}
      error={error}
      isDocumentTypeRequired={isDocumentTypeRequired}
    />
  );
};

export default SingleFileUpload;
